import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useViewMode, ViewMode } from '../contexts/ViewModeContext';
import { useAuth } from '../contexts/AuthContext';
import { FREE_TRIAL_ENABLED } from '../services/config';
import Dashboard from './Dashboard';
import ChatbotGenerationView from './ChatbotGenerationView';
import FunctionChaining from './FunctionChaining';
import { FirstLoginModal } from '../components/FirstLoginModal';
import { FreeCreditsWelcomeBanner } from '../components/FreeCreditsWelcomeBanner';

const LINKABLE_VIEW_MODES: ViewMode[] = ['developer', 'chaining', 'chatbot_generation'];

export const DashboardRouter: React.FC = () => {
  const { viewMode, setViewMode, hasCompletedOnboarding } = useViewMode();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const chainLink = searchParams.get('chain');
  const viewParam = searchParams.get('view') as ViewMode | null;
  const linkedView = viewParam && LINKABLE_VIEW_MODES.includes(viewParam) ? viewParam : null;

  useEffect(() => {
    if (chainLink && viewMode !== 'chaining') setViewMode('chaining');
  }, [chainLink, viewMode, setViewMode]);

  useEffect(() => {
    if (!viewParam) return;
    if (linkedView && viewMode !== linkedView) {
      setViewMode(linkedView);
      return;
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      return next;
    }, { replace: true });
  }, [viewParam, linkedView, viewMode, setViewMode, setSearchParams]);
  const [customerData, setCustomerData] = useState<any>(null);
  const [showFreeCreditsModal, setShowFreeCreditsModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  // Fetch customer data
  useEffect(() => {
    const loadCustomerData = async () => {
      if (!user) return;
      
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/v2/customers/current', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setCustomerData(result.data);
        }
      } catch (err) {
        console.error('Error loading customer data:', err);
      }
    };
    
    loadCustomerData();
  }, [user]);

  // Determine which modals to show - only runs once when onboarding needed
  useEffect(() => {
    // Only evaluate modals if user hasn't completed onboarding AND we have customer data
    if (!hasCompletedOnboarding && customerData) {
      const shouldShowCreditsModal = checkShouldShowCreditsModal(customerData);
      
      if (shouldShowCreditsModal) {
        // Show free credits modal first
        setShowFreeCreditsModal(true);
        setShowOnboardingModal(false);
      } else {
        // Skip to onboarding modal
        setShowFreeCreditsModal(false);
        setShowOnboardingModal(true);
      }
    } else if (hasCompletedOnboarding) {
      // User has completed onboarding - ensure modals are closed
      setShowFreeCreditsModal(false);
      setShowOnboardingModal(false);
    }
  }, [hasCompletedOnboarding, customerData]);

  const checkShouldShowCreditsModal = (customerData: any): boolean => {
    // Don't show if free trial is disabled
    if (!FREE_TRIAL_ENABLED) return false;
    
    // Check localStorage - if we've already shown it (using either old or new key), don't show again
    const hasShownModal = localStorage.getItem('freeCreditsModalShown') === 'true' ||
                          localStorage.getItem('freeCreditsWelcomeBannerShown') === 'true';
    if (hasShownModal) {
      console.log('[DashboardRouter] Free credits modal already shown (localStorage check)');
      return false;
    }

    // Only show to users on free tier with active status (they have the signup credits)
    const isFreeUser = customerData.subscription?.tier_id === 'free' && 
                       customerData.subscription?.status === 'active';
    if (!isFreeUser) {
      console.log('[DashboardRouter] Not a free tier user, skipping modal');
      return false;
    }

    // Check if account is less than 5 minutes old
    const createdAt = customerData.created_at;
    if (!createdAt) {
      console.log('[DashboardRouter] No created_at timestamp, skipping modal');
      return false;
    }

    try {
      const accountCreatedTime = new Date(createdAt).getTime();
      const currentTime = new Date().getTime();
      const minutesSinceCreation = (currentTime - accountCreatedTime) / (1000 * 60);
      
      console.log(`[DashboardRouter] Account age: ${minutesSinceCreation.toFixed(2)} minutes`);
      
      // Only show if account is less than 5 minutes old
      if (minutesSinceCreation < 5) {
        console.log('[DashboardRouter] Account is new, showing free credits modal');
        return true;
      }
      
      console.log('[DashboardRouter] Account too old, skipping modal');
      return false;
    } catch (error) {
      console.error('[DashboardRouter] Error checking account age:', error);
      return false;
    }
  };

  const handleFreeCreditsClose = () => {
    console.log('[DashboardRouter] Closing free credits modal, marking as shown');
    // Mark as shown in localStorage - this prevents it from showing again
    localStorage.setItem('freeCreditsModalShown', 'true');
    setShowFreeCreditsModal(false);
    // Now show the onboarding modal
    setShowOnboardingModal(true);
  };

  // Show modals in sequence during onboarding
  if (!hasCompletedOnboarding) {
    return (
      <>
        <FreeCreditsWelcomeBanner 
          open={showFreeCreditsModal} 
          onClose={handleFreeCreditsClose}
        />
        <FirstLoginModal open={showOnboardingModal} />
      </>
    );
  }

  // Children read and strip their own prefill params, so mount them only after `view` is consumed.
  if (viewParam) return null;

  // Route to the appropriate view based on viewMode
  switch (viewMode) {
    case 'developer':
      return <Dashboard />;
    case 'studio':
      return <Navigate to="/dashboard/playground" replace />;
    case 'chatbot_generation':
      return <ChatbotGenerationView />;
    case 'chaining':
      return <FunctionChaining />;
    default:
      return <Dashboard />;
  }
};

export default DashboardRouter;

